import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { BoardViewportState } from "../../lib/board/boardViewport";
import { clientToStage, worldToClient } from "../../lib/board/boardViewport";
import {
  newVoteOptionId,
  VOTE_HEIGHT_MAX,
  VOTE_HEIGHT_MIN,
  VOTE_OPTION_LABEL_MAX,
  VOTE_OPTIONS_MAX,
  VOTE_OPTIONS_MIN,
  VOTE_WIDTH_MAX,
  VOTE_WIDTH_MIN,
  type VoteBlock,
  type VoteOption,
} from "../../hooks/useBoardVote";

type Option = { id: string; label: string };

type Props = {
  blocks: VoteBlock[];
  countsFor: (voteId: string) => Record<string, number>;
  viewportRef: RefObject<HTMLDivElement | null>;
  viewport: BoardViewportState;
  getLiveViewport: () => BoardViewportState;
  layoutTick: number;
  canManage: boolean;
  isOwnerOf: (voteId: string) => boolean;
  onCast: (voteId: string, optionId: string) => void;
  onEnd: (voteId: string) => void;
  onMove: (voteId: string, worldX: number, worldY: number) => void;
  onResize: (voteId: string, widthPx: number, heightPx: number) => void;
  onTitleChange: (voteId: string, question: string) => void;
  /** Автор поменял варианты ответа. */
  onOptionsChange?: (voteId: string, options: VoteOption[]) => void;
};

function useCameraViewport(
  viewport: BoardViewportState,
  getLiveViewport: () => BoardViewportState,
  layoutTick: number
) {
  const [live, setLive] = useState(viewport);

  useEffect(() => {
    setLive(getLiveViewport());
  }, [viewport, layoutTick, getLiveViewport]);

  useEffect(() => {
    const onLive = () => setLive(getLiveViewport());
    window.addEventListener("wb:live-viewport", onLive);
    return () => window.removeEventListener("wb:live-viewport", onLive);
  }, [getLiveViewport]);

  return live;
}

function VoteCard({
  block,
  counts,
  viewportRef,
  cam,
  canManage,
  onCast,
  onEnd,
  onMove,
  onResize,
  onTitleChange,
  onOptionsChange,
}: {
  block: VoteBlock;
  counts: Record<string, number>;
  viewportRef: RefObject<HTMLDivElement | null>;
  cam: BoardViewportState;
  canManage: boolean;
  onCast: (optionId: string) => void;
  onEnd: () => void;
  onMove: (worldX: number, worldY: number) => void;
  onResize: (widthPx: number, heightPx: number) => void;
  onTitleChange: (question: string) => void;
  onOptionsChange?: (options: VoteOption[]) => void;
}) {
  const dragRef = useRef<{
    pointerId: number;
    grabOffsetX: number;
    grabOffsetY: number;
  } | null>(null);
  const resizeRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origW: number;
    origH: number;
  } | null>(null);
  const localWorldRef = useRef<{ x: number; y: number } | null>(null);
  const localSizeRef = useRef<{ w: number; h: number } | null>(null);
  const lastSentRef = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [localWorld, setLocalWorld] = useState<{ x: number; y: number } | null>(null);
  const [localSize, setLocalSize] = useState<{ w: number; h: number } | null>(null);
  const [titleDraft, setTitleDraft] = useState(block.question);
  /** Правка вариантов — только у автора: поля вместо кнопок голосования. */
  const [editingOptions, setEditingOptions] = useState(false);
  const canEditOptions = canManage && Boolean(onOptionsChange);
  /** Только что добавленный вариант — в фокус, с выделенным текстом. */
  const [justAddedId, setJustAddedId] = useState<string | null>(null);
  const totalVotes = Object.values(counts).reduce((a, b) => a + b, 0);

  const worldX = localWorld?.x ?? block.worldX;
  const worldY = localWorld?.y ?? block.worldY;
  const widthPx = localSize?.w ?? block.widthPx;
  const heightPx = localSize?.h ?? block.heightPx;

  useEffect(() => {
    setTitleDraft(block.question);
  }, [block.question]);

  useEffect(() => {
    if (dragging) return;
    setLocalWorld(null);
    localWorldRef.current = null;
  }, [block.worldX, block.worldY, dragging]);

  useEffect(() => {
    if (resizing) return;
    setLocalSize(null);
    localSizeRef.current = null;
  }, [block.widthPx, block.heightPx, resizing]);

  const vpEl = viewportRef.current;
  const screen = (() => {
    if (!vpEl) return null;
    const rect = vpEl.getBoundingClientRect();
    return worldToClient(worldX, worldY, rect, cam);
  })();

  const clientToWorld = useCallback(
    (clientX: number, clientY: number) => {
      const el = viewportRef.current;
      if (!el) return { x: worldX, y: worldY };
      return clientToStage(clientX, clientY, el.getBoundingClientRect(), cam);
    },
    [cam, viewportRef, worldX, worldY]
  );

  const publishMove = useCallback(
    (x: number, y: number, force = false) => {
      const now = performance.now();
      if (!force && now - lastSentRef.current < 50) return;
      lastSentRef.current = now;
      onMove(x, y);
    },
    [onMove]
  );

  const publishResize = useCallback(
    (w: number, h: number, force = false) => {
      const now = performance.now();
      if (!force && now - lastSentRef.current < 50) return;
      lastSentRef.current = now;
      onResize(w, h);
    },
    [onResize]
  );

  const onPointerDownDrag = useCallback(
    (ev: React.PointerEvent) => {
      if (ev.button !== 0) return;
      const target = ev.target as HTMLElement;
      if (target.closest("button, input, a")) return;
      ev.preventDefault();
      ev.stopPropagation();
      ev.currentTarget.setPointerCapture(ev.pointerId);
      const grab = clientToWorld(ev.clientX, ev.clientY);
      dragRef.current = {
        pointerId: ev.pointerId,
        grabOffsetX: grab.x - worldX,
        grabOffsetY: grab.y - worldY,
      };
      setDragging(true);
      const start = { x: worldX, y: worldY };
      localWorldRef.current = start;
      setLocalWorld(start);
    },
    [clientToWorld, worldX, worldY]
  );

  const onPointerMoveDrag = useCallback(
    (ev: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== ev.pointerId) return;
      const pt = clientToWorld(ev.clientX, ev.clientY);
      const next = {
        x: pt.x - d.grabOffsetX,
        y: pt.y - d.grabOffsetY,
      };
      localWorldRef.current = next;
      setLocalWorld(next);
      publishMove(next.x, next.y);
    },
    [clientToWorld, publishMove]
  );

  const onPointerUpDrag = useCallback(
    (ev: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== ev.pointerId) return;
      dragRef.current = null;
      try {
        ev.currentTarget.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      const pos = localWorldRef.current ?? { x: worldX, y: worldY };
      publishMove(pos.x, pos.y, true);
      setDragging(false);
    },
    [publishMove, worldX, worldY]
  );

  const onPointerDownResize = useCallback(
    (ev: React.PointerEvent) => {
      if (ev.button !== 0) return;
      ev.preventDefault();
      ev.stopPropagation();
      ev.currentTarget.setPointerCapture(ev.pointerId);
      resizeRef.current = {
        pointerId: ev.pointerId,
        startX: ev.clientX,
        startY: ev.clientY,
        origW: widthPx,
        origH: heightPx,
      };
      setResizing(true);
      const start = { w: widthPx, h: heightPx };
      localSizeRef.current = start;
      setLocalSize(start);
    },
    [heightPx, widthPx]
  );

  const onPointerMoveResize = useCallback(
    (ev: React.PointerEvent) => {
      const d = resizeRef.current;
      if (!d || d.pointerId !== ev.pointerId) return;
      const next = {
        w: Math.min(
          VOTE_WIDTH_MAX,
          Math.max(VOTE_WIDTH_MIN, d.origW + (ev.clientX - d.startX))
        ),
        h: Math.min(
          VOTE_HEIGHT_MAX,
          Math.max(VOTE_HEIGHT_MIN, d.origH + (ev.clientY - d.startY))
        ),
      };
      localSizeRef.current = next;
      setLocalSize(next);
      publishResize(next.w, next.h);
    },
    [publishResize]
  );

  const onPointerUpResize = useCallback(
    (ev: React.PointerEvent) => {
      const d = resizeRef.current;
      if (!d || d.pointerId !== ev.pointerId) return;
      resizeRef.current = null;
      try {
        ev.currentTarget.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      const size = localSizeRef.current ?? { w: widthPx, h: heightPx };
      publishResize(size.w, size.h, true);
      setResizing(false);
    },
    [heightPx, publishResize, widthPx]
  );

  function commitTitle() {
    const next = titleDraft.trim();
    if (!next || next === block.question) {
      setTitleDraft(block.question);
      return;
    }
    onTitleChange(next);
  }

  if (!screen) return null;

  return (
    <div
      className={`wb-board-vote${dragging || resizing ? " is-dragging" : ""}`}
      role="dialog"
      aria-label={`Опрос ${block.blockIndex}`}
      style={{
        position: "fixed",
        left: screen.x,
        top: screen.y,
        width: widthPx,
        minHeight: heightPx,
        // Фиксируем высоту только во время/после ручного ресайза — иначе кнопка
        // «Закрыть» могла обрезаться overflow:hidden у слишком низкого окна.
        height: resizing || localSize ? heightPx : "auto",
        right: "auto",
        bottom: "auto",
        transform: "none",
      }}
    >
      <div
        className="wb-board-vote__drag"
        onPointerDown={onPointerDownDrag}
        onPointerMove={onPointerMoveDrag}
        onPointerUp={onPointerUpDrag}
        onPointerCancel={onPointerUpDrag}
        title="Перетащить по доске"
      >
        <span className="wb-board-vote__badge">Блок {block.blockIndex}</span>
        <span className="wb-board-vote__total">{totalVotes} гол.</span>
      </div>

      <div className="wb-board-vote__body">
        {canManage ? (
          <input
            className="wb-board-vote__title-input"
            value={titleDraft}
            maxLength={120}
            aria-label="Название опроса"
            placeholder={`Опрос ${block.blockIndex}`}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              }
            }}
          />
        ) : (
          <strong className="wb-board-vote__title">{block.question}</strong>
        )}

        {editingOptions && canEditOptions ? (
          <div className="wb-board-vote__options wb-board-vote__options--edit">
            {block.options.map((o: Option, i) => {
              const votes = counts[o.id] ?? 0;
              return (
                <div key={o.id} className="wb-board-vote__edit-row">
                  <input
                    className="wb-board-vote__edit-input"
                    defaultValue={o.label}
                    maxLength={VOTE_OPTION_LABEL_MAX}
                    aria-label={`Вариант ${i + 1}`}
                    // Новый вариант сразу в фокусе — можно печатать.
                    autoFocus={o.id === justAddedId}
                    onFocus={(e) => {
                      if (o.id === justAddedId) e.target.select();
                    }}
                    onBlur={(e) => {
                      const label = e.target.value.trim() || `Вариант ${i + 1}`;
                      if (label === o.label) return;
                      onOptionsChange?.(
                        block.options.map((x) => (x.id === o.id ? { ...x, label } : x))
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="wb-board-vote__edit-remove"
                    disabled={block.options.length <= VOTE_OPTIONS_MIN}
                    title={
                      votes > 0
                        ? `Убрать вариант — ${votes} гол. за него пропадут`
                        : "Убрать вариант"
                    }
                    aria-label={`Убрать вариант ${i + 1}`}
                    onClick={() => onOptionsChange?.(block.options.filter((x) => x.id !== o.id))}
                  >
                    ×
                  </button>
                </div>
              );
            })}
            {block.options.length < VOTE_OPTIONS_MAX ? (
              <button
                type="button"
                className="wb-board-vote__edit-add"
                onClick={() => {
                  const id = newVoteOptionId();
                  setJustAddedId(id);
                  onOptionsChange?.([
                    ...block.options,
                    { id, label: `Вариант ${block.options.length + 1}` },
                  ]);
                }}
              >
                + Добавить вариант
              </button>
            ) : null}
          </div>
        ) : (
        <div className="wb-board-vote__options">
          {block.options.map((o: Option) => {
            const n = counts[o.id] ?? 0;
            const pct = totalVotes > 0 ? Math.round((n / totalVotes) * 100) : 0;
            const selected = block.myChoice === o.id;
            return (
              <button
                key={o.id}
                type="button"
                className={`wb-board-vote__option${selected ? " is-selected" : ""}`}
                onClick={() => onCast(o.id)}
              >
                <span className="wb-board-vote__option-label">{o.label}</span>
                <span className="wb-board-vote__option-count">
                  {n} · {pct}%
                </span>
                <span
                  className="wb-board-vote__option-bar"
                  style={{ width: `${pct}%` }}
                  aria-hidden
                />
              </button>
            );
          })}
        </div>
        )}
      </div>

      <div className="wb-board-vote__footer">
        {canManage ? (
          <>
            {canEditOptions ? (
              <button
                type="button"
                className="wb-board-vote__edit-toggle"
                aria-pressed={editingOptions}
                onClick={() => setEditingOptions((v) => !v)}
              >
                {editingOptions ? "Готово" : "Изменить варианты"}
              </button>
            ) : null}
            <button type="button" className="wb-board-vote__end" onClick={onEnd}>
              Закрыть опрос
            </button>
          </>
        ) : (
          <p className="wb-board-vote__hint">Выберите вариант — можно сменить до конца опроса</p>
        )}
      </div>

      <div
        className="wb-board-vote__resize"
        role="separator"
        aria-label="Изменить размер"
        title="Изменить размер"
        onPointerDown={onPointerDownResize}
        onPointerMove={onPointerMoveResize}
        onPointerUp={onPointerUpResize}
        onPointerCancel={onPointerUpResize}
      />
    </div>
  );
}

export function BoardVotePanel({
  blocks,
  countsFor,
  viewportRef,
  viewport,
  getLiveViewport,
  layoutTick,
  canManage: _canManage,
  isOwnerOf,
  onCast,
  onEnd,
  onMove,
  onResize,
  onTitleChange,
  onOptionsChange,
}: Props) {
  const cam = useCameraViewport(viewport, getLiveViewport, layoutTick);

  if (!blocks.length) return null;

  return (
    <div className="wb-board-vote-stack" aria-live="polite">
      {blocks.map((block) => (
        <VoteCard
          key={block.voteId}
          block={block}
          counts={countsFor(block.voteId)}
          viewportRef={viewportRef}
          cam={cam}
          canManage={isOwnerOf(block.voteId)}
          onCast={(optionId) => onCast(block.voteId, optionId)}
          onEnd={() => onEnd(block.voteId)}
          onMove={(x, y) => onMove(block.voteId, x, y)}
          onResize={(w, h) => onResize(block.voteId, w, h)}
          onTitleChange={(q) => onTitleChange(block.voteId, q)}
          onOptionsChange={
            onOptionsChange ? (options) => onOptionsChange(block.voteId, options) : undefined
          }
        />
      ))}
    </div>
  );
}
